import React, { useMemo, useState } from 'react';
import { FmlxButton, FmlxCheckBox } from 'fmlx-common-ui';
import { useSelectArchivingResults } from 'redux/hooks/archiving';
import { useRMDispatch } from 'redux/hooks';
import { clearArchivingResults } from 'redux/reducers/archivingReducer';
import { dismissArchivingResultsThunk } from 'redux/thunks/archiving';
import { CustomTable } from './controls/customTable';
import { ARCHIVING_STORAGE_ERROR_DESCRIPTION, ResultsTableColumns } from './constants';
import './styles.css';
import { ArchivingJobExperimentStatusDto } from 'business/models/Models';
import { ReactComponent as WarningIcon } from 'Icons/Warning.svg';
import {
  getArchivingJobErrorMessage,
  getArchivingResultRowStatusLabel,
  useIsArchivingJobWaitingForStorage,
} from './hooks';
import {
  getArchivingArchivedLabel,
  getArchivingStatusLabel,
} from './hooks/useArchivingProgressHub';
import { isDismissableArchivingResultStatus } from './utils';
import { useSelectArchivingBackgroundJob } from 'redux/hooks/backgroundJobs';
import { showAlertSnackBar } from 'utils/Global';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';

const formatDate = (value?: Date) => value?.toLocaleDateString('en-US');

export const ResultsTable = () => {
  const archivingResults = useSelectArchivingResults();
  const archivingJob = useSelectArchivingBackgroundJob();
  const isJobWaitingForStorage = useIsArchivingJobWaitingForStorage();
  const dispatch = useRMDispatch();
  const [showArchived, setShowArchived] = useState(true);
  const [showScheduled, setShowScheduled] = useState(true);
  const [showFailed, setShowFailed] = useState(true);

  const archivedCount = useMemo(
    () =>
      archivingResults.filter(
        (row) => row.status === ArchivingJobExperimentStatusDto.Completed,
      ).length,
    [archivingResults],
  );

  const scheduledCount = useMemo(
    () =>
      archivingResults.filter(
        (row) => row.status === ArchivingJobExperimentStatusDto.Pending,
      ).length,
    [archivingResults],
  );

  const failedCount = useMemo(
    () =>
      archivingResults.filter(
        (row) => row.status === ArchivingJobExperimentStatusDto.Failed,
      ).length,
    [archivingResults],
  );

  // Only checked statuses are shown; Running is always shown regardless of the
  // checkboxes since a row actively being archived should never disappear mid-job.
  const visibleResults = useMemo(() => {
    return archivingResults.filter((row) => {
      switch (row.status) {
        case ArchivingJobExperimentStatusDto.Completed:
          return showArchived;
        case ArchivingJobExperimentStatusDto.Pending:
          return showScheduled;
        case ArchivingJobExperimentStatusDto.Failed:
          return showFailed;
        case ArchivingJobExperimentStatusDto.Running:
        default:
          return true;
      }
    });
  }, [archivingResults, showArchived, showFailed, showScheduled]);

  const data = useMemo(
    () =>
      visibleResults.map((item) => {
        const statusLabel = getArchivingResultRowStatusLabel(
          item.status,
          isJobWaitingForStorage,
        );

        return {
          name: item.experimentName,
          originalLocation: item.originalLocation,
          currentLocation: item.currentLocation,
          dateCreated: formatDate(item.dateCreated),
          inspectionDate: formatDate(item.inspectionDate),
          dateArchived: formatDate(item.archivedDate),
          status:
            item.status === ArchivingJobExperimentStatusDto.Failed &&
            item.errorMessage ? (
              <span title={item.errorMessage}>{statusLabel}</span>
            ) : (
              statusLabel
            ),
        };
      }),
    [isJobWaitingForStorage, visibleResults],
  );

  const toggleStatusFilter = (status: ArchivingJobExperimentStatusDto) => {
    switch (status) {
      case ArchivingJobExperimentStatusDto.Completed:
        setShowArchived((current) => !current);
        return;
      case ArchivingJobExperimentStatusDto.Failed:
        setShowFailed((current) => !current);
        return;
      default:
        setShowScheduled((current) => !current);
    }
  };

  const handleClearResults = async () => {
    const dismissableExperimentIds = archivingResults
      .filter((row) => isDismissableArchivingResultStatus(row.status))
      .map((row) => row.experimentId);

    if (dismissableExperimentIds.length === 0) {
      dispatch(clearArchivingResults());
      return;
    }

    try {
      await dispatch(dismissArchivingResultsThunk(dismissableExperimentIds)).unwrap();
      dispatch(clearArchivingResults());
    } catch (error) {
      showAlertSnackBar(
        EAlertType.ERROR,
        ESnackBarPosition.TOP,
        typeof error === 'string' ? error : 'Failed to clear archiving results.',
      );
    }
  };

  const archivedLabel = getArchivingArchivedLabel(archivingJob);
  const statusLabel = getArchivingStatusLabel(archivingJob);

  const storageBannerMessage = isJobWaitingForStorage
    ? `${getArchivingJobErrorMessage(
        archivingJob,
      )} ${ARCHIVING_STORAGE_ERROR_DESCRIPTION}`
    : '';

  return (
    <div className="tab-content-height w-full flex flex-col" data-testid="results-table">
      {isJobWaitingForStorage && (
        <div className="archiving-warning-banner" data-testid="archiving-warning-banner">
          <div className="archiving-warning-banner-content">
            <WarningIcon className="archiving-warning-banner-icon" aria-hidden="true" />
            <h4 className="archiving-warning-banner-message">{storageBannerMessage}</h4>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between border-b border-gray-300 bg-white px-4 py-2">
        <div className="flex items-center gap-8">
          <FmlxCheckBox
            checked={showArchived}
            label={`Archived (${archivedCount})`}
            onChange={() => toggleStatusFilter(ArchivingJobExperimentStatusDto.Completed)}
          />
          <FmlxCheckBox
            checked={showScheduled}
            label={`Scheduled (${scheduledCount})`}
            onChange={() => toggleStatusFilter(ArchivingJobExperimentStatusDto.Pending)}
          />
          <FmlxCheckBox
            checked={showFailed}
            label={`Failed (${failedCount})`}
            onChange={() => toggleStatusFilter(ArchivingJobExperimentStatusDto.Failed)}
          />
        </div>
        <FmlxButton
          label="Clear Results"
          onClick={handleClearResults}
          size="sm"
          type={FmlxButton.Type.BASIC}
        />
      </div>
      <div className="flex-1 w-full min-h-0">
        <CustomTable
          columns={ResultsTableColumns}
          data={data}
          enableSelection={false}
          id="archiving-results-table"
        />
      </div>
      <div
        className="w-full flex items-center border-t border-b border-gray-300 bg-white px-4 py-2 font-sans text-xs text-gray-700 select-none"
        data-testid="archiving-progress-status"
      >
        <div>
          <span className="text-gray-500">Archived:</span>{' '}
          <span className="font-semibold text-gray-900">{archivedLabel}</span>
        </div>
        <div className="ml-auto">
          <span className="text-gray-500">Status:</span>{' '}
          <span className="font-semibold text-gray-900">{statusLabel}</span>
        </div>
      </div>
    </div>
  );
};
